import React, { useState, useEffect, useCallback } from 'react'
import { TextField } from '@material-ui/core'
import { withFormsy } from 'formsy-react'
import _ from '@lodash'
import { useIntl, FormattedMessage } from 'react-intl'
import { isEmailValid } from 'commons/email'
import effects from 'app/effects/effects'
import * as R from 'ramda'
import { networkErrorToMessage, errorTypes } from 'commons/error'

const performCheckEmail = _.debounce(
  (
    isValidFormat,
    email,
    onValidating,
    onValidateComplete,
    formatMessage,
    isExistence,
  ) => {
    if (isValidFormat) {
      onValidateComplete(undefined)
      onValidating()
      effects.apis
        .checkEmail({ email, isExistence })
        .then(() => onValidateComplete(undefined))
        .catch((error) =>
          R.compose(
            onValidateComplete,
            R.prop('message'),
            networkErrorToMessage(formatMessage),
          )(error, errorTypes.emailField),
        )
    } else {
      onValidateComplete(undefined)
    }
  },
  500,
)

export const EmailInputValidationType = {
  official: 'official',
  existence: 'existence',
}

function EmailInputFormsy(props) {
  const importedProps = _.pick(props, [
    'autoComplete',
    'autoFocus',
    'children',
    'className',
    'defaultValue',
    'disabled',
    'FormHelperTextProps',
    'fullWidth',
    'id',
    'InputLabelProps',
    'inputProps',
    'InputProps',
    'inputRef',
    'label',
    'multiline',
    'name',
    'onBlur',
    'onChange',
    'onFocus',
    'placeholder',
    'required',
    'rows',
    'rowsMax',
    'select',
    'SelectProps',
    'type',
    'variant',
  ])

  const validationType = R.defaultTo(
    EmailInputValidationType.existence,
    props.validationType,
  )

  const errorMessage = props.errorMessage
  const value = props.value
  const { formatMessage } = useIntl()
  const label = R.unless(R.isNil, (id) => formatMessage({ id }), props.labelId)
  const placeholder = R.unless(
    R.isNil,
    (id) => formatMessage({ id }),
    props.placeholderId,
  )

  const [isValidFormat, setValidFormat] = useState()
  const [isValidating, setIsValidating] = useState(false)

  /* eslint-disable react-hooks/exhaustive-deps */
  const onInvalid = useCallback(
    R.compose(
      R.defaultTo(() => {}),
      R.prop('onInvalid'),
    )(props),
    [],
  )
  /* eslint-enable react-hooks/exhaustive-deps */
  const [checkEmailError, setCheckEmailError] = useState()

  useEffect(() => {
    const isExistence =
      validationType === EmailInputValidationType.existence || undefined
    performCheckEmail(
      isValidFormat,
      value,
      () => {
        onInvalid({ code: 'validating' })
        setIsValidating(true)
      },
      (error) => {
        setCheckEmailError(error)
        onInvalid(error)
        setIsValidating(false)
      },
      formatMessage,
      isExistence,
    )
  }, [formatMessage, isValidFormat, value, validationType, onInvalid])

  function changeValue(event) {
    const newValue = event.currentTarget.value
    const isValidFormat = isEmailValid(newValue)

    props.setValue(newValue)
    setValidFormat(isValidFormat)

    if (props.onChange) {
      props.onChange(event)
    }
  }

  const checkingMessage = isValidating ? (
    <FormattedMessage id="emailInput.validating.label" />
  ) : null

  const isRestrict = R.defaultTo(false, props.isRestrict)

  return (
    <TextField
      {...importedProps}
      type="email"
      onChange={changeValue}
      value={value}
      error={Boolean(errorMessage) || (isRestrict && Boolean(checkEmailError))}
      helperText={errorMessage || checkEmailError || checkingMessage}
      label={label}
      placeholder={placeholder}
    />
  )
}

export default React.memo(withFormsy(EmailInputFormsy))
